import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, BookmarkCheck, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  deleteWorkspaceItem,
  fetchWorkspace,
  saveWorkspaceItem,
  type WorkspaceKind,
} from '@/services/workspace';

const BUTTON =
  'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-50';

function useOwnItem(agency: string, kind: WorkspaceKind, itemKey: string) {
  return useQuery({
    queryKey: ['workspace', agency, kind, itemKey],
    queryFn: () => fetchWorkspace({ agency, kind, key: itemKey }),
    select: (list) => list.items[0] ?? null,
  });
}

function useWorkspaceMutations(agency: string, kind: WorkspaceKind, itemKey: string) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workspace'] });
  const onError = (error: unknown) =>
    toast.error(t('workspace.failed'), {
      description: error instanceof Error ? error.message : undefined,
    });

  const save = useMutation({
    mutationFn: (input: { label: string; note: string }) =>
      saveWorkspaceItem({ kind, agency, key: itemKey, ...input }),
    onSuccess: refresh,
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteWorkspaceItem(id),
    onSuccess: refresh,
    onError,
  });
  return { save, remove };
}

/**
 * Mark a case and keep a private note on it.
 *
 * Keyed on the process number as the agency prints it, never on a database id,
 * so the mark survives an ETL reload. The note is the signed-in user's alone;
 * the server filters every read by owner.
 */
export function MarkCaseControl({
  agency,
  caseNumber,
}: {
  agency: string;
  caseNumber: string;
}) {
  const { t } = useTranslation('common');
  const { data: item } = useOwnItem(agency, 'case', caseNumber);
  const { save, remove } = useWorkspaceMutations(agency, 'case', caseNumber);
  const [note, setNote] = useState('');

  useEffect(() => {
    setNote(item?.note ?? '');
  }, [item?.id, item?.note]);

  if (!item) {
    return (
      <button
        type="button"
        className={BUTTON}
        disabled={save.isPending}
        onClick={() => save.mutate({ label: caseNumber, note: '' })}
      >
        <Bookmark className="h-3.5 w-3.5" aria-hidden />
        {t('workspace.markCase')}
      </button>
    );
  }

  return (
    <div className="w-full max-w-md space-y-2 rounded-md border p-3" data-workspace-marked>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
          <BookmarkCheck className="h-4 w-4 text-primary" aria-hidden />
          {t('workspace.marked')}
        </span>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          disabled={remove.isPending}
          onClick={() => remove.mutate(item.id)}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          {t('workspace.remove')}
        </button>
      </div>
      <label className="block text-xs text-muted-foreground" htmlFor="workspace-note">
        {t('workspace.noteLabel')}
      </label>
      <textarea
        id="workspace-note"
        value={note}
        maxLength={5000}
        rows={3}
        onChange={(event) => setNote(event.target.value)}
        className="w-full rounded-md border bg-background p-2 text-sm text-foreground"
      />
      <button
        type="button"
        className={BUTTON}
        disabled={save.isPending || note === item.note}
        onClick={() => save.mutate({ label: item.label, note })}
      >
        {t('workspace.saveNote')}
      </button>
    </div>
  );
}

/**
 * Save the search the screen is showing, by its query string.
 *
 * The same string the screen and its exports are built from, so reopening a saved
 * search runs the query that was saved and not a reconstruction of it.
 */
export function SaveSearchControl({
  agency,
  queryString,
  defaultLabel,
}: {
  agency: string;
  queryString: string;
  defaultLabel: string;
}) {
  const { t } = useTranslation('common');
  const { data: item } = useOwnItem(agency, 'search', queryString);
  const { save, remove } = useWorkspaceMutations(agency, 'search', queryString);
  const [label, setLabel] = useState(defaultLabel);

  useEffect(() => {
    setLabel(item?.label || defaultLabel);
  }, [item?.id, item?.label, defaultLabel]);

  if (item) {
    return (
      <button
        type="button"
        className={BUTTON}
        disabled={remove.isPending}
        onClick={() => remove.mutate(item.id)}
        data-workspace-saved
      >
        <BookmarkCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
        {t('workspace.searchSaved')}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <input
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        maxLength={200}
        aria-label={t('workspace.searchLabel')}
        className="w-48 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      />
      <button
        type="button"
        className={BUTTON}
        disabled={save.isPending || !queryString}
        onClick={() => save.mutate({ label: label.trim() || defaultLabel, note: '' })}
      >
        <Bookmark className="h-3.5 w-3.5" aria-hidden />
        {t('workspace.saveSearch')}
      </button>
    </span>
  );
}
